import { PaymentAccountTypeSelect } from "@components/inputs/PaymentAccountTypeSelect";
import RHFController from "@components/inputs/RHFController";
import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer";
import { DEFAULT_CARD_TYPES } from "@constants/bank-funding-profile";
import useContactLinks from "@hooks/useContactLinks";
import { useFundingProfileFormConfig } from "@hooks/useFundingProfileFormConfig";
import { useMemberSettings } from "@hooks/useMemberSettings";
import {
  CardType,
  Checkbox,
  cn,
  FundingProfileType,
  MemberBankFundingType,
  MemberSettingFundingMode,
  MemberSettingNameMode,
  Select,
  TextField,
} from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BankFormConfig, InnerFormProps } from "../../types";
import InlineErrorWithContact from "../InlineErrorWithContact";

const fundingMode = MemberSettingFundingMode.BankOnly;

const BankAccountInnerForm = ({
  errors,
  control,
  isLoading,
  displayLegalName,
  allowEditDefault,
  fieldHelperTextManager,
}: InnerFormProps) => {
  const { t } = useTranslation();
  const { openContact } = useContactLinks();
  const { bankFormConfig = {} } = useFundingProfileFormConfig();
  const { showPhoneInputPrefix, phonePrefix, phoneMinLength, phoneMaxLength, phoneStartWith } = useMemberSettings();

  const pixKeyType = useWatch({ control, name: "pixKeyType" });
  const pidType = useWatch({ control, name: "pidType" });
  const bankTypeOption = useWatch({ control, name: "bankTypeOption" });
  const ifscChecked = useWatch({ control, name: "ifscChecked" });
  const bankType = useWatch({ control, name: "bankType" });
  const cardType = useWatch({ control, name: "cardType" });

  // Helper to check if error is a backend error (prefixed with "backend:")
  const isBackendError = (errorMessage?: string): boolean => {
    return !!errorMessage?.startsWith("backend:");
  };

  // Helper to get the actual error message key (strip "backend:" prefix if present)
  const getErrorMessageKey = (errorMessage?: string): string | undefined => {
    if (!errorMessage) return undefined;
    return errorMessage.startsWith("backend:") ? errorMessage.slice(8) : errorMessage;
  };

  // Check if accountNo error requires contact CS (backend errors only)
  // IFSC errors are shown on accountNo field because duplicate check is IFSC + accountNo combination
  const accountNoErrorKey = getErrorMessageKey(errors.accountNo?.message);
  const showLinkedError =
    (isBackendError(errors.accountNo?.message) && !!accountNoErrorKey?.endsWith("is not valid")) ||
    accountNoErrorKey === t(`${fundingMode} is linked to another user.`);

  // Check if unique identifier errors require contact CS (errors ending with "is not valid")
  const showPanError = !!errors.pan?.message?.endsWith("is not valid");
  const showCpfError = !!errors.cpf?.message?.endsWith("is not valid");
  const showCurpError = !!errors.curp?.message?.endsWith("is not valid");
  const showPidError = !!errors.pidNumber?.message?.endsWith("is not valid");

  const accountNoLabel = useMemo(() => {
    // Prefer config-driven labels if available
    const labelsByMethod = bankFormConfig?.labelsByMethod as Record<string, { accountNumber?: string }> | undefined;
    if (labelsByMethod && bankTypeOption && labelsByMethod[bankTypeOption]?.accountNumber) {
      return t(labelsByMethod[bankTypeOption]?.accountNumber as string);
    }
    // Default
    return t("account number");
  }, [bankFormConfig, bankTypeOption, t]);

  const sortedDynamicKeys = useMemo(() => {
    const candidateKeys = Object.keys(bankFormConfig) as (keyof Omit<
      BankFormConfig,
      "paymentMethodOptions" | "labelsByMethod" | "ifscToggleAvailable"
    >)[];
    const enabledKeys = candidateKeys.filter((k) => bankFormConfig[k]?.enabled !== false);
    return enabledKeys.sort((a, b) => {
      const pa = bankFormConfig[a]?.sortPriority ?? 9999;
      const pb = bankFormConfig[b]?.sortPriority ?? 9999;
      return pa - pb;
    });
  }, [bankFormConfig]);

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const renderDynamicFields = useCallback(() => {
    const rendered = new Set<string>();
    return sortedDynamicKeys.map((key) => {
      if (rendered.has(key as string)) return null;
      switch (key) {
        case "bank": {
          if (bankFormConfig?.bank?.enabled === false) return null;
          return (
            <label
              key={key as string}
              className="flex flex-col"
            >
              <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.accountType })}>
                {t("bank")}
              </span>
              <RHFController
                control={control}
                name="accountType"
              >
                <PaymentAccountTypeSelect
                  disabled={isLoading}
                  accountType={FundingProfileType.Bank}
                />
              </RHFController>
            </label>
          );
        }
        case "bankTypeOption": {
          if (
            bankFormConfig?.bankTypeOption?.enabled === false ||
            !Array.isArray(bankFormConfig?.paymentMethodOptions) ||
            (bankFormConfig?.paymentMethodOptions?.length || 0) === 0
          )
            return null;
          return (
            <label
              key={key as string}
              className="flex flex-col"
            >
              <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.bankTypeOption })}>
                {t("payment method")}
              </span>
              <RHFController
                control={control}
                name="bankTypeOption"
              >
                <Select
                  placeholder={t("please select")}
                  options={bankFormConfig?.paymentMethodOptions?.map((opt: string) => ({
                    id: opt,
                    name: opt === "upi" ? t("upi") : t("bank transfer"),
                  }))}
                  size={"lg"}
                  classes={{
                    "root&": "gap-0",
                    "label&": "text-content-base text-base",
                    "placeHolder&": cn("text-content-soft text-start w-56", { "text-content-base": !!bankTypeOption }),
                    "helperText&": "!mt-1 !text-xs my-0 px-0",
                    "selectedOption&": "!text-content-base text-base",
                    "errorText&": "text-error-base text-xs",
                    "option&": "cursor-pointer hover:bg-white hover:bg-opacity-5",
                    "optionsContainer&": "divide-stroke-base divide-y",
                  }}
                  aria-label="Bank account payment method select"
                />
              </RHFController>
            </label>
          );
        }
        case "fullName": {
          if (!displayLegalName || bankFormConfig?.fullName?.enabled === false) return null;
          return (
            <MemberSettingsRenderer
              key={key as string}
              feature="nameMode"
              match={MemberSettingNameMode.FullInFirst}
            >
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("accountName"),
                  })}
                >
                  {t("full name")}
                </span>
                <RHFController
                  control={control}
                  name="accountName"
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"John Doe"}
                    disabled={isLoading}
                    aria-label="Bank account full name input"
                    data-element-id="input-bank-account-inner-fullname"
                  />
                </RHFController>
              </label>
            </MemberSettingsRenderer>
          );
        }
        case "firstName":
        case "lastName": {
          if (!displayLegalName) return null;
          if (rendered.has("firstName") || rendered.has("lastName")) return null;
          rendered.add("firstName");
          rendered.add("lastName");
          return (
            <MemberSettingsRenderer
              key="firstLast"
              feature="nameMode"
              match={MemberSettingNameMode.FirstLast}
            >
              <div className="grid grid-cols-2 gap-4">
                {bankFormConfig?.firstName?.enabled !== false && (
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("firstName"),
                      })}
                    >
                      {t("first name")}
                    </span>
                    <RHFController
                      control={control}
                      name="firstName"
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"John"}
                        disabled={isLoading}
                        aria-label="Bank account first name input"
                        data-element-id="input-bank-account-inner-firstname"
                      />
                    </RHFController>
                  </label>
                )}
                {bankFormConfig?.lastName?.enabled !== false && (
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("lastName"),
                      })}
                    >
                      {t("last name")}
                    </span>
                    <RHFController
                      control={control}
                      name="lastName"
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"Doe"}
                        disabled={isLoading}
                        aria-label="Bank account last name input"
                        data-element-id="input-bank-account-inner-lastname"
                      />
                    </RHFController>
                  </label>
                )}
              </div>
            </MemberSettingsRenderer>
          );
        }
        case "cpf": {
          if (!bankFormConfig?.cpf?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("cpf"),
                  })}
                >
                  {t("cpf number")}
                </span>
                <RHFController
                  control={control}
                  name="cpf"
                  hideErrorText={showCpfError}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"231.002.999-00"}
                    disabled={isLoading}
                    aria-label="Bank account cpf number input"
                    data-element-id="input-bank-account-cpf"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showCpfError}
                onContact={handleOpenContactLink}
                messageKey={errors.cpf?.message}
              />
            </div>
          );
        }
        case "pan": {
          if (!bankFormConfig?.pan?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("pan"),
                  })}
                >
                  {t("pan number")}
                </span>
                <RHFController
                  control={control}
                  name="pan"
                  hideErrorText={showPanError}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"AFZPK7190K"}
                    disabled={isLoading}
                    classes={{
                      "input&": "uppercase",
                    }}
                    aria-label="Bank account pan input"
                    data-element-id="input-bank-account-pan"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showPanError}
                onContact={handleOpenContactLink}
                messageKey={errors.pan?.message}
              />
            </div>
          );
        }
        case "curp": {
          if (!bankFormConfig?.curp?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("curp"),
                  })}
                >
                  {t("curp number")}
                </span>
                <RHFController
                  control={control}
                  name="curp"
                  hideErrorText={showCurpError}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"HEGG560427MVZRRL04"}
                    disabled={isLoading}
                    aria-label="Bank account curp number input"
                    data-element-id="input-bank-account-curp"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showCurpError}
                onContact={handleOpenContactLink}
                messageKey={errors.curp?.message}
              />
            </div>
          );
        }
        case "clabeNumber": {
          if (!bankFormConfig?.clabeNumber?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
                  })}
                >
                  {t("clabe number")}
                </span>
                <RHFController
                  control={control}
                  name="accountNo"
                  hideErrorText={showLinkedError}
                  helperTextParams={{ fundingMode: fundingMode || "" }}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"001-12345678912-3"}
                    disabled={isLoading}
                    aria-label="Bank account clabe number input"
                    data-element-id="input-bank-account-clabe"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showLinkedError}
                onContact={handleOpenContactLink}
                messageKey={accountNoErrorKey}
              />
            </div>
          );
        }
        case "pixKey": {
          if (!bankFormConfig?.pixKey?.enabled) return null;
          return (
            <div
              key={key as string}
              className="flex flex-col gap-4"
            >
              <label className="flex flex-col">
                <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.pixKeyType })}>
                  {t("pix key type")}
                </span>
                <RHFController
                  control={control}
                  name="pixKeyType"
                >
                  <Select
                    placeholder={t("please select")}
                    options={[
                      { id: "cpf", name: t("cpf") },
                      { id: "email", name: t("email") },
                      { id: "phone", name: t("phone number") },
                      { id: "evp", name: t("random") },
                    ]}
                    size={"lg"}
                    classes={{
                      "root&": "gap-0",
                      "label&": "text-content-base text-base",
                      "placeHolder&": cn("text-content-soft text-start w-56", { "text-content-base": !!pixKeyType }),
                      "helperText&": "!mt-1 !text-xs my-0 px-0",
                      "selectedOption&": "!text-content-base text-base",
                      "errorText&": "text-error-base text-xs",
                      "option&": "cursor-pointer hover:bg-white hover:bg-opacity-5",
                      "optionsContainer&": "divide-stroke-base divide-y",
                    }}
                    aria-label="Bank account pix key type select"
                  />
                </RHFController>
              </label>
              {pixKeyType === "cpf" && (
                <div>
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
                      })}
                    >
                      {t("pix key")}: {t("cpf")}
                    </span>
                    <RHFController
                      control={control}
                      name="accountNo"
                      hideErrorText={showLinkedError}
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"231.002.999-00"}
                        disabled={isLoading}
                        classes={{ "input&": "w-full truncate" }}
                        aria-label="Bank account pix key cpf input"
                        data-element-id="input-bank-account-pix-cpf"
                      />
                    </RHFController>
                  </label>
                  <InlineErrorWithContact
                    show={showLinkedError}
                    onContact={handleOpenContactLink}
                    messageKey={accountNoErrorKey}
                  />
                </div>
              )}
              {pixKeyType === "email" && (
                <div>
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
                      })}
                    >
                      {t("pix key")}: {t("email")}
                    </span>
                    <RHFController
                      control={control}
                      name="accountNo"
                      hideErrorText={showLinkedError}
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"john.doe@example.com"}
                        disabled={isLoading}
                        aria-label="Bank account pix key email input"
                        data-element-id="input-bank-account-pix-email"
                      />
                    </RHFController>
                  </label>
                  <InlineErrorWithContact
                    show={showLinkedError}
                    onContact={handleOpenContactLink}
                    messageKey={accountNoErrorKey}
                  />
                </div>
              )}
              {pixKeyType === "phone" && (
                <div>
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
                      })}
                    >
                      {t("pix key")}: {t("phone number")}
                    </span>
                    <RHFController
                      control={control}
                      name="accountNo"
                      hideErrorText={showLinkedError}
                      helperTextParams={{
                        minLength: phoneMinLength,
                        maxLength: phoneMaxLength,
                        startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : phoneStartWith,
                      }}
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"123456789123456"}
                        disabled={isLoading}
                        classes={{
                          "input&": cn("w-full truncate", { "ps-[48px]": showPhoneInputPrefix }),
                          "prefixContainer&": showPhoneInputPrefix
                            ? "absolute start-3 top-[12px] text-content-base"
                            : "",
                        }}
                        aria-label="Bank account pix key phone input"
                        prefix={showPhoneInputPrefix ? phonePrefix : undefined}
                        data-element-id="input-bank-account-pix-phone"
                      />
                    </RHFController>
                  </label>
                  <InlineErrorWithContact
                    show={showLinkedError}
                    onContact={handleOpenContactLink}
                    messageKey={accountNoErrorKey}
                  />
                </div>
              )}
              {pixKeyType === "evp" && (
                <div>
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
                      })}
                    >
                      {t("pix key")}: {t("random")}
                    </span>
                    <RHFController
                      control={control}
                      name="accountNo"
                      hideErrorText={showLinkedError}
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"123e4567-e89b-12d3-a456-426614174000"}
                        disabled={isLoading}
                        classes={{ "input&": "w-full truncate" }}
                        aria-label="Bank account pix key random input"
                        data-element-id="input-bank-account-pix-random"
                      />
                    </RHFController>
                  </label>
                  <InlineErrorWithContact
                    show={showLinkedError}
                    onContact={handleOpenContactLink}
                    messageKey={accountNoErrorKey}
                  />
                </div>
              )}
            </div>
          );
        }
        case "pid": {
          if (!bankFormConfig?.pid?.enabled) return null;

          const getPlaceholder = () => {
            switch (pidType) {
              case "dni":
                return "12345678";
              case "ce":
                return "A12345678";
              case "pen_pas":
                return "A12345678";
              case "ruc":
                return "12345678901";
              case "cc":
                return "1234567890";
              case "nit":
                return "123456789012";
              case "cop_pas":
                return "P1234567";
              default:
                return "";
            }
          };

          const getLabel = () => {
            switch (pidType) {
              case "dni":
                return `${t("pid number")}: ${t("dni")}`;
              case "ce":
                return `${t("pid number")}: ${t("ce")}`;
              case "pen_pas":
                return `${t("pid number")}: ${t("pen_pas")}`;
              case "ruc":
                return `${t("pid number")}: ${t("ruc")}`;
              case "cc":
                return `${t("pid number")}: ${t("cc")}`;
              case "nit":
                return `${t("pid number")}: ${t("nit")}`;
              case "cop_pas":
                return `${t("pid number")}: ${t("cop_pas")}`;
              default:
                return t("pid number");
            }
          };

          // Use pre-computed PID type options from config
          const pidTypeOptions = (bankFormConfig?.pid?.pidTypeOptions || []).map((option) => ({
            id: option.id,
            name: t(option.name),
          }));

          return (
            <div
              key={key as string}
              className="flex flex-col gap-4"
            >
              <label className="flex flex-col">
                <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.pidType })}>
                  {t("pid type")}
                </span>
                <RHFController
                  control={control}
                  name="pidType"
                >
                  <Select
                    placeholder={t("please select")}
                    options={pidTypeOptions}
                    size={"lg"}
                    classes={{
                      "root&": "gap-0",
                      "label&": "text-content-base text-base",
                      "placeHolder&": cn("text-content-soft text-start w-56", { "text-content-base": !!pidType }),
                      "helperText&": "!mt-1 !text-xs my-0 px-0",
                      "selectedOption&": "!text-content-base text-base",
                      "errorText&": "text-error-base text-xs",
                      "option&": "cursor-pointer hover:bg-white hover:bg-opacity-5",
                      "optionsContainer&": "divide-stroke-base divide-y",
                    }}
                    aria-label="Bank account pid type select"
                  />
                </RHFController>
              </label>
              {pidType && (
                <label className="flex flex-col">
                  <span
                    className={cn("mb-0.5 text-sm text-content-base", {
                      "text-error-base": fieldHelperTextManager?.shouldShowError("pidNumber"),
                    })}
                  >
                    {getLabel()}
                  </span>
                  <RHFController
                    control={control}
                    name="pidNumber"
                    hideErrorText={showPidError}
                    fieldHelperTextManager={fieldHelperTextManager}
                  >
                    <TextField
                      placeholder={getPlaceholder()}
                      disabled={isLoading}
                      classes={{
                        "input&": cn({
                          uppercase: pidType === "ce" || pidType === "pen_pas" || pidType === "cop_pas",
                        }),
                      }}
                      aria-label="Bank account pid number input"
                      data-element-id="input-bank-account-pid"
                    />
                  </RHFController>
                  <InlineErrorWithContact
                    show={showPidError}
                    onContact={handleOpenContactLink}
                    messageKey={errors.pidNumber?.message}
                  />
                </label>
              )}
            </div>
          );
        }
        case "accountNumber": {
          if (!bankFormConfig?.accountNumber?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
                  })}
                >
                  {accountNoLabel}
                </span>
                <RHFController
                  control={control}
                  name="accountNo"
                  hideErrorText={showLinkedError}
                  helperTextParams={{ fundingMode: fundingMode || "" }}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={
                      bankTypeOption
                        ? t(bankFormConfig?.labelsByMethod?.[bankTypeOption]?.placeholder || "")
                        : "123-456789-012"
                    }
                    disabled={isLoading}
                    classes={{ "input&": "w-full truncate" }}
                    aria-label="Bank account account number input"
                    data-element-id="input-bank-account-number-inner"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showLinkedError}
                onContact={handleOpenContactLink}
                messageKey={accountNoErrorKey}
              />
            </div>
          );
        }
        case "ifsc": {
          if (!bankFormConfig?.ifscToggleAvailable || bankTypeOption !== "bank") return null;
          return (
            <div key={key as string}>
              <label
                className="inline-flex items-center justify-start gap-[2px]"
                aria-label="Bank account IFSC checkbox"
              >
                <RHFController
                  control={control}
                  name="ifscChecked"
                >
                  <Checkbox
                    variant="outlined"
                    classes={{
                      input_outlined:
                        "checked:border-accentBrand-500 checked:bg-accentBrand-500 border-stroke-base border-2",
                    }}
                    aria-label="Bank account IFSC checkbox"
                    // TODO: Allow uncheck/check later when supported
                    disabled
                  />
                </RHFController>
                <p className="text-sm text-content-base">{t("ifsc")}</p>
              </label>
              {ifscChecked && (
                <label className="mt-2 flex flex-col">
                  <RHFController
                    control={control}
                    name="ifsc"
                    fieldHelperTextManager={fieldHelperTextManager}
                  >
                    <TextField
                      placeholder={t("enter ifsc code")}
                      disabled={isLoading}
                      data-element-id="input-bank-account-ifsc"
                    />
                  </RHFController>
                </label>
              )}
            </div>
          );
        }
        case "cci": {
          if (!bankFormConfig?.cci?.enabled || bankType !== MemberBankFundingType.CCI) return null;
          return (
            <label
              key={key as string}
              className="flex flex-col"
            >
              <span
                className={cn("mb-0.5 text-sm text-content-base", {
                  "text-error-base": fieldHelperTextManager?.shouldShowError("cci"),
                })}
              >
                {t("cci code")}
              </span>
              <RHFController
                control={control}
                name="cci"
                fieldHelperTextManager={fieldHelperTextManager}
              >
                <TextField
                  placeholder={"12345678901234567890"}
                  disabled={isLoading}
                  maxLength={20}
                  data-element-id="input-bank-account-cci"
                  aria-label="Bank account CCI code input"
                />
              </RHFController>
            </label>
          );
        }
        case "cardType": {
          if (!bankFormConfig?.cardType?.enabled) return null;

          // Get card type options from config (server-provided) or fallback to defaults
          const cardTypeOptions = bankFormConfig?.cardType?.cardTypeOptions ?? DEFAULT_CARD_TYPES;

          const getCardTypeLabel = (value: string) => {
            switch (value) {
              case CardType.Checking:
                return t("checking account");
              case CardType.Saving:
                return t("saving account");
              case CardType.Wallet:
                return t("wallet");
              default:
                return value;
            }
          };

          return (
            <label
              key={key as string}
              className="flex flex-col"
            >
              <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.cardType })}>
                {t("account type")}
              </span>
              <RHFController
                control={control}
                name="cardType"
              >
                <Select
                  placeholder={t("please select")}
                  options={cardTypeOptions.map((option) => ({
                    id: option,
                    name: getCardTypeLabel(option),
                  }))}
                  size={"lg"}
                  classes={{
                    "root&": "gap-0",
                    "label&": "text-content-base text-base",
                    "placeHolder&": cn("text-content-soft text-start w-56", { "text-content-base": !!cardType }),
                    "helperText&": "!mt-1 !text-xs my-0 px-0",
                    "selectedOption&": "!text-content-base text-base",
                    "errorText&": "text-error-base text-xs",
                    "option&": "cursor-pointer hover:bg-white hover:bg-opacity-5",
                    "optionsContainer&": "divide-stroke-base divide-y",
                  }}
                  aria-label="Bank account card type select"
                />
              </RHFController>
            </label>
          );
        }
        default:
          return null;
      }
    });
  }, [
    sortedDynamicKeys,
    bankFormConfig,
    displayLegalName,
    errors,
    control,
    isLoading,
    t,
    bankTypeOption,
    pixKeyType,
    pidType,
    showPhoneInputPrefix,
    phonePrefix,
    ifscChecked,
    handleOpenContactLink,
    accountNoLabel,
    accountNoErrorKey,
    phoneMaxLength,
    phoneMinLength,
    phoneStartWith,
    showLinkedError,
    showPanError,
    showCpfError,
    showCurpError,
    showPidError,
    fieldHelperTextManager,
    bankType,
    cardType,
  ]);

  return (
    <>
      {renderDynamicFields()}
      {/* DEFAULT CHECKBOX */}
      <div>
        <label
          className="inline-flex items-center justify-start gap-[2px]"
          aria-label="Bank account default checkbox"
        >
          <RHFController
            control={control}
            name="isDefault"
          >
            <Checkbox
              variant="outlined"
              classes={{
                input_outlined: "checked:border-accentBrand-500 checked:bg-accentBrand-500 border-stroke-base border-2",
                "root&": !allowEditDefault ? "opacity-30" : "",
              }}
              disabled={!allowEditDefault}
              aria-label="Bank account default checkbox"
            />
          </RHFController>

          <p
            className="text-sm text-content-base"
            aria-label="Bank account default checkbox"
          >
            {t("set as default")}
          </p>
        </label>
      </div>
    </>
  );
};

export default BankAccountInnerForm;
